import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface Mailvorlage {
  vorlage: string;
  label: string;
  platzhalter: string[];
  betreff: string;
  text: string;
  signatur_aktiv: boolean;
  angepasst: boolean;
  standard_betreff: string;
  standard_text: string;
  standard_signatur: boolean;
}

interface Props {
  onFehler: (m: string | null) => void;
}

const fehlerText = (err: unknown): string =>
  axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';

/**
 * E-Mail-Texte-Reiter der Firma-Sektion (`/api/firma/mailvorlagen`).
 * Betreff und Text je Mail (Belegversand und Konto-Mails) mit Platzhaltern wie
 * `{belegnummer}`; die Signatur kommt aus den Firmenstammdaten und lässt sich
 * abschalten. „Vorschau“ rendert den aktuellen Entwurf serverseitig mit
 * Beispielwerten — genau so, wie der Versand rendert.
 */
const FirmaMailvorlagen: React.FC<Props> = ({ onFehler }) => {
  const [vorlagen, setVorlagen] = useState<Mailvorlage[]>([]);
  const [loading, setLoading] = useState(true);
  const [auswahl, setAuswahl] = useState<string>('RECHNUNG');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [signatur, setSignatur] = useState(true);
  const [vorschau, setVorschau] = useState<{ betreff: string; html: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const aktuelle = vorlagen.find(v => v.vorlage === auswahl);

  const uebernehmen = (v: Mailvorlage) => {
    setBetreff(v.betreff);
    setText(v.text);
    setSignatur(v.signatur_aktiv);
    setVorschau(null);
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get<Mailvorlage[]>(`${API_URL}/api/firma/mailvorlagen`)
      .then(r => {
        setVorlagen(r.data);
        const v = r.data.find(x => x.vorlage === 'RECHNUNG') ?? r.data[0];
        if (v) {
          setAuswahl(v.vorlage);
          setBetreff(v.betreff);
          setText(v.text);
          setSignatur(v.signatur_aktiv);
        }
      })
      .catch((err: unknown) => onFehler(`E-Mail-Texte konnten nicht geladen werden: ${fehlerText(err)}`))
      .finally(() => setLoading(false));
  }, [onFehler]);

  useEffect(() => { load(); }, [load]);

  const wechseln = (key: string) => {
    const v = vorlagen.find(x => x.vorlage === key);
    if (!v) return;
    onFehler(null);
    setGespeichert(false);
    setAuswahl(key);
    uebernehmen(v);
  };

  const ersetzeVorlage = (neu: Mailvorlage) =>
    setVorlagen(prev => prev.map(v => (v.vorlage === neu.vorlage ? neu : v)));

  const speichern = async () => {
    onFehler(null);
    setBusy(true);
    try {
      const r = await axios.put<Mailvorlage>(`${API_URL}/api/firma/mailvorlagen/${auswahl}`,
        { betreff, text, signatur_aktiv: signatur });
      ersetzeVorlage(r.data);
      uebernehmen(r.data);
      setGespeichert(true);
      window.setTimeout(() => setGespeichert(false), 2000);
    } catch (err: unknown) {
      onFehler(`Speichern fehlgeschlagen: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const zuruecksetzen = async () => {
    if (!window.confirm('Eigenen Text verwerfen und den Standardtext wiederherstellen?')) return;
    onFehler(null);
    setBusy(true);
    try {
      const r = await axios.delete<Mailvorlage>(`${API_URL}/api/firma/mailvorlagen/${auswahl}`);
      ersetzeVorlage(r.data);
      uebernehmen(r.data);
    } catch (err: unknown) {
      onFehler(`Zurücksetzen fehlgeschlagen: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const zeigeVorschau = async () => {
    onFehler(null);
    setBusy(true);
    try {
      const r = await axios.post<{ betreff: string; html: string }>(
        `${API_URL}/api/firma/mailvorlagen/${auswahl}/vorschau`,
        { betreff, text, signatur_aktiv: signatur });
      setVorschau(r.data);
    } catch (err: unknown) {
      setVorschau(null);
      onFehler(`Vorschau nicht möglich: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const platzhalterEinfuegen = (p: string) => {
    const el = textRef.current;
    const marke = `{${p}}`;
    if (!el) { setText(t => t + marke); return; }
    const von = el.selectionStart ?? text.length;
    const bis = el.selectionEnd ?? text.length;
    setText(text.slice(0, von) + marke + text.slice(bis));
    window.setTimeout(() => { el.focus(); el.setSelectionRange(von + marke.length, von + marke.length); }, 0);
  };

  if (loading) return <div>Lade E-Mail-Texte…</div>;
  if (!aktuelle) return <div>Keine Mailvorlagen vorhanden.</div>;

  const geaendert = betreff !== aktuelle.betreff || text !== aktuelle.text || signatur !== aktuelle.signatur_aktiv;

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Betreff und Text der E-Mails, die das System verschickt. Platzhalter in geschweiften
        Klammern werden beim Versand durch Werte aus dem Beleg und den Firmenstammdaten
        ersetzt. Die Signatur (Firmenname, Geschäftsführer, Telefon, E-Mail, Website) kommt
        automatisch aus den Stammdaten.
      </p>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16, maxWidth: 320 }}>
        E-Mail
        <select value={auswahl} onChange={e => wechseln(e.target.value)} style={{ padding: '7px 10px' }}>
          {vorlagen.map(v => (
            <option key={v.vorlage} value={v.vorlage}>
              {v.label}{v.angepasst ? ' (angepasst)' : ''}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        Betreff
        <input
          type="text"
          value={betreff}
          onChange={e => setBetreff(e.target.value)}
          style={{ padding: '7px 10px' }}
        />
      </label>

      <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--ink-2)' }}>
        Platzhalter einfügen:{' '}
        {aktuelle.platzhalter.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => platzhalterEinfuegen(p)}
            style={{ margin: '0 4px 4px 0', padding: '2px 8px', fontSize: 12, cursor: 'pointer',
                     border: '1px solid var(--bone-3)', background: 'var(--bone-2)', color: 'var(--ink)' }}
          >
            {`{${p}}`}
          </button>
        ))}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        Text
        <textarea
          ref={textRef}
          rows={10}
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ padding: '8px 10px', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, color: 'var(--ink)' }}>
        <input type="checkbox" checked={signatur} onChange={e => setSignatur(e.target.checked)} />
        Signatur aus den Firmenstammdaten anhängen
      </label>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
        <button type="button" className="k-btn k-btn--primary" onClick={speichern} disabled={busy || !geaendert}>
          Speichern
        </button>
        <button type="button" className="k-btn" onClick={zeigeVorschau} disabled={busy}>
          Vorschau
        </button>
        <button type="button" className="k-btn" onClick={zuruecksetzen} disabled={busy || !aktuelle.angepasst}>
          Auf Standard zurücksetzen
        </button>
        {gespeichert && <span style={{ color: 'var(--green-500)', fontSize: 13 }}>✓ Gespeichert</span>}
      </div>

      {vorschau && (
        <div style={{ border: '1px solid var(--bone-3)', background: '#fff', padding: 16 }}>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
            <strong>Betreff:</strong> {vorschau.betreff}
            <span style={{ marginLeft: 12, color: '#999' }}>(mit Beispielwerten)</span>
          </div>
          <iframe
            title="Vorschau der E-Mail"
            sandbox=""
            srcDoc={vorschau.html}
            style={{ width: '100%', height: 340, border: '1px solid var(--bone-3)' }}
          />
        </div>
      )}
    </div>
  );
};

export default FirmaMailvorlagen;
